import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { reportsApi } from '../../services/reports';
import {
  canAccessReports,
  type AuditReportSection,
  type StatusReportSection,
  type ReportTemplate,
} from '@traaker/shared';

/**
 * Report template editor (route /reports/audit). NOT a report viewer — it
 * configures the per-tenant "house style": which sections appear in every
 * generated report. Reports are produced from /reports/library; this page only
 * defines their shape. A section is ON unless explicitly toggled off; the
 * editor seeds unset toggles to ON so a fresh tenant sees the full report.
 *
 * Owner/admin only persist; the API enforces the same.
 */

const AUDIT_SECTION_META: Record<AuditReportSection, { labelKey: string; helpKey: string }> = {
  scores: { labelKey: 'auditReport.audit.scores.label', helpKey: 'auditReport.audit.scores.help' },
  website: { labelKey: 'auditReport.audit.website.label', helpKey: 'auditReport.audit.website.help' },
  websiteReadability: { labelKey: 'auditReport.audit.websiteReadability.label', helpKey: 'auditReport.audit.websiteReadability.help' },
  websiteSignals: { labelKey: 'auditReport.audit.websiteSignals.label', helpKey: 'auditReport.audit.websiteSignals.help' },
  narrative: { labelKey: 'auditReport.audit.narrative.label', helpKey: 'auditReport.audit.narrative.help' },
  authority: { labelKey: 'auditReport.audit.authority.label', helpKey: 'auditReport.audit.authority.help' },
  referral: { labelKey: 'auditReport.audit.referral.label', helpKey: 'auditReport.audit.referral.help' },
  earnedMedia: { labelKey: 'auditReport.audit.earnedMedia.label', helpKey: 'auditReport.audit.earnedMedia.help' },
  competitorRanking: { labelKey: 'auditReport.audit.competitorRanking.label', helpKey: 'auditReport.audit.competitorRanking.help' },
  competitors: { labelKey: 'auditReport.audit.competitors.label', helpKey: 'auditReport.audit.competitors.help' },
  promptsUsed: { labelKey: 'auditReport.audit.promptsUsed.label', helpKey: 'auditReport.audit.promptsUsed.help' },
};

const STATUS_SECTION_META: Record<StatusReportSection, { labelKey: string; helpKey: string }> = {
  evolution: { labelKey: 'auditReport.status.evolution.label', helpKey: 'auditReport.status.evolution.help' },
  tasksCompleted: { labelKey: 'auditReport.status.tasksCompleted.label', helpKey: 'auditReport.status.tasksCompleted.help' },
  tasksInProgress: { labelKey: 'auditReport.status.tasksInProgress.label', helpKey: 'auditReport.status.tasksInProgress.help' },
  focusTasks: { labelKey: 'auditReport.status.focusTasks.label', helpKey: 'auditReport.status.focusTasks.help' },
};

const AUDIT_ORDER: AuditReportSection[] = [
  'scores', 'website', 'websiteReadability', 'websiteSignals', 'narrative', 'authority', 'referral', 'earnedMedia', 'competitorRanking', 'competitors', 'promptsUsed',
];
/** Sections that default OFF (opt-in): on only when the template sets `=== true`. */
const AUDIT_OPT_IN = new Set<AuditReportSection>(['promptsUsed', 'websiteReadability', 'websiteSignals']);
const STATUS_ORDER: StatusReportSection[] = ['evolution', 'tasksCompleted', 'tasksInProgress', 'focusTasks'];

export default function AuditReport() {
  const { t } = useTranslation();
  const { tenant, user } = useAuth();
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const reportsEnabled = canAccessReports(tenant?.tier, tenant?.settings?.featureFlags?.reportsEnabled);
  const canEdit =
    user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';

  useEffect(() => {
    if (!reportsEnabled) return;
    setLoading(true);
    reportsApi
      .getTemplate()
      .then((r) => setTemplate(r.template))
      .catch((e) => setError(e?.message || t('auditReport.error.load')))
      .finally(() => setLoading(false));
  }, [reportsEnabled, t]);

  // Most sections are enabled unless explicitly false; opt-in sections
  // (e.g. promptsUsed) are disabled unless explicitly true.
  const auditOn = (s: AuditReportSection) =>
    AUDIT_OPT_IN.has(s) ? template?.audit?.[s] === true : template?.audit?.[s] !== false;
  const statusOn = (s: StatusReportSection) => template?.status?.[s] !== false;

  const auditCount = useMemo(() => AUDIT_ORDER.filter(auditOn).length, [template]);
  const statusCount = useMemo(() => STATUS_ORDER.filter(statusOn).length, [template]);

  const toggleAudit = (s: AuditReportSection, on: boolean) =>
    setTemplate((t) => ({ audit: { ...(t?.audit || {}), [s]: on }, status: { ...(t?.status || {}) } }));
  const toggleStatus = (s: StatusReportSection, on: boolean) =>
    setTemplate((t) => ({ audit: { ...(t?.audit || {}) }, status: { ...(t?.status || {}), [s]: on } }));

  const save = async () => {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      const { template: saved } = await reportsApi.saveTemplate(template);
      setTemplate(saved);
      setSavedAt(Date.now());
    } catch (e: any) {
      setError(e?.body?.message || e?.message || t('auditReport.error.save'));
    } finally {
      setSaving(false);
    }
  };

  if (!reportsEnabled) return <DisabledNotice />;

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('auditReport.title')}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('auditReport.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          disabled={!canEdit || saving || !template}
          onClick={save}
          sx={{ flexShrink: 0, mt: 0.5 }}
        >
          {saving ? t('auditReport.saving') : t('auditReport.saveTemplate')}
        </Button>
      </Stack>

      {savedAt && (
        <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mb: 2 }}>
          {t('auditReport.savedNotice')}
        </Alert>
      )}
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('auditReport.readOnlyNotice')}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {template && (
        <Stack spacing={3}>
          <TemplateSection
            icon={<AssessmentIcon sx={{ color: '#7C3AED' }} />}
            title={t('auditReport.section.auditTitle')}
            subtitle={t('auditReport.section.auditSubtitle')}
            countLabel={t('auditReport.sectionCount', { n: auditCount, total: AUDIT_ORDER.length })}
            disabled={!canEdit}
            rows={AUDIT_ORDER.map((s) => ({
              key: s,
              label: t(AUDIT_SECTION_META[s].labelKey),
              help: t(AUDIT_SECTION_META[s].helpKey),
              on: auditOn(s),
              onChange: (v: boolean) => toggleAudit(s, v),
            }))}
          />
          <TemplateSection
            icon={<InsightsIcon sx={{ color: '#00DDB3' }} />}
            title={t('auditReport.section.statusTitle')}
            subtitle={t('auditReport.section.statusSubtitle')}
            countLabel={t('auditReport.sectionCount', { count: statusCount, total: STATUS_ORDER.length })}
            disabled={!canEdit}
            rows={STATUS_ORDER.map((s) => ({
              key: s,
              label: t(STATUS_SECTION_META[s].labelKey),
              help: t(STATUS_SECTION_META[s].helpKey),
              on: statusOn(s),
              onChange: (v: boolean) => toggleStatus(s, v),
            }))}
          />
        </Stack>
      )}
    </Box>
  );
}

function TemplateSection({
  icon,
  title,
  subtitle,
  countLabel,
  rows,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  countLabel: string;
  disabled: boolean;
  rows: Array<{ key: string; label: string; help: string; on: boolean; onChange: (v: boolean) => void }>;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 2 }}>
          {icon}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
          <Chip size="small" label={countLabel} variant="outlined" />
        </Stack>
        <Divider />
        <Box>
          {rows.map((r, i) => (
            <Box
              key={r.key}
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderTop: i === 0 ? 'none' : '1px solid',
                borderColor: 'divider',
                opacity: r.on ? 1 : 0.6,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                <Typography variant="caption" color="text.secondary">{r.help}</Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={r.on}
                    disabled={disabled}
                    onChange={(e) => r.onChange(e.target.checked)}
                  />
                }
                label={r.on ? t('auditReport.included') : t('auditReport.hidden')}
                labelPlacement="start"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary', minWidth: 52 } }}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function DisabledNotice() {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        {t('auditReport.title')}
      </Typography>
      <Alert severity="info">
        {t('auditReport.disabledNotice')}
      </Alert>
    </Box>
  );
}
